// src/pages/InstallPage.jsx
// Route: /install  (public — no sign-in, so a QR code or a texted link can land here)
//
// One page, four audiences:
//   iPhone Safari   — illustrated Add to Home Screen steps (there is no install prompt on iOS)
//   iPhone, other   — "open this in Safari" first (in-app browsers and Chrome can't be relied on)
//   Android Chrome  — a real Install button, with menu steps as the fallback
//   Desktop         — a QR code to scan with a phone, printable for a clinic poster

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import QrCode from '../components/QrCode';
import previuLogo from '../assets/previu-logo.jpeg';
import { getPlatform, isStandalone } from '../utils/platform';

function Step({ n, title, children }) {
  return (
    <li className="install-step">
      <span className="install-step-num" aria-hidden="true">{n}</span>
      <div>
        <div className="install-step-title">{title}</div>
        {children}
      </div>
    </li>
  );
}

// A sketch of Safari's toolbar with the Share button picked out, so people can
// find it. Newer iPhones may tuck it inside the ⋯ menu — the step says so.
function SafariBarMock() {
  return (
    <div className="install-bar" aria-hidden="true">
      <span className="install-bar-slot" />
      <span className="install-bar-slot" />
      <span className="install-bar-slot install-bar-slot--active"><Icon name="share" size={22} /></span>
      <span className="install-bar-slot" />
      <span className="install-bar-slot" />
    </div>
  );
}

function CopyLink({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      window.prompt('Copy this link', url); // clipboard blocked (some in-app browsers)
    }
  }, [url]);
  return (
    <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
      {copied ? 'Link copied ✓' : 'Copy link'}
    </button>
  );
}

function IosSafariSteps() {
  return (
    <ol className="install-steps">
      <Step n="1" title="Tap the Share button">
        <SafariBarMock />
        <p className="notice-text">
          It&apos;s the square with an arrow pointing up, in the bar at the bottom of Safari. On some
          iPhones it&apos;s inside the <strong>⋯</strong> menu — tap that first.
        </p>
      </Step>
      <Step n="2" title={<>Choose <strong>Add to Home Screen</strong> <Icon name="addToHome" size={16} /></>}>
        <p className="notice-text">Scroll down in the list that opens if you don&apos;t see it.</p>
      </Step>
      <Step n="3" title={<>Tap <strong>Add</strong></>}>
        <p className="notice-text">If you see &ldquo;Open as Web App&rdquo;, leave it switched on.</p>
      </Step>
      <Step n="4" title="Open Previu from your Home Screen">
        <p className="notice-text">
          Use the new Previu icon from now on. Notifications only work when you open it from there.
        </p>
      </Step>
    </ol>
  );
}

function IosOtherSteps({ url }) {
  return (
    <>
      <div className="alert alert-warning">
        Installing works best from <strong>Safari</strong>. You&apos;re in a different browser or inside another app.
      </div>
      <ol className="install-steps">
        <Step n="1" title="Copy this link">
          <div className="install-actions"><CopyLink url={url} /></div>
        </Step>
        <Step n="2" title="Open Safari and paste it">
          <p className="notice-text">Find the Safari app (a blue compass) on your phone and paste the link into its address bar.</p>
        </Step>
        <Step n="3" title="Follow the steps shown there">
          <p className="notice-text">Safari will show you how to add Previu to your Home Screen.</p>
        </Step>
      </ol>
    </>
  );
}

function AndroidSteps({ canPrompt, onInstall, webview, url }) {
  if (webview) {
    return (
      <>
        <div className="alert alert-warning">
          Installing works best from <strong>Chrome</strong>. You&apos;re inside another app&apos;s browser.
        </div>
        <ol className="install-steps">
          <Step n="1" title="Copy this link"><div className="install-actions"><CopyLink url={url} /></div></Step>
          <Step n="2" title="Open Chrome and paste it" />
        </ol>
      </>
    );
  }
  return (
    <>
      {canPrompt && (
        <div className="install-actions">
          <button type="button" className="btn btn-primary" onClick={onInstall}>Install Previu</button>
        </div>
      )}
      <ol className="install-steps">
        <Step n="1" title={<>Tap the <strong>menu</strong> <Icon name="menuDots" size={16} /> in Chrome</>}>
          <p className="notice-text">It&apos;s at the top right of the screen.</p>
        </Step>
        <Step n="2" title={<>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong></>} />
        <Step n="3" title="Open Previu from your Home Screen" />
      </ol>
    </>
  );
}

function DesktopQr({ url }) {
  return (
    <div className="install-qr">
      <QrCode value={url} size={220} label="QR code to open the Previu install page on your phone" />
      <ol className="install-steps">
        <Step n="1" title="Open the camera app on your phone" />
        <Step n="2" title="Point it at this code and tap the link that appears">
          <p className="notice-text">On iPhone it opens in Safari, which is what you need.</p>
        </Step>
        <Step n="3" title="Follow the steps on your phone" />
      </ol>
      <div className="install-actions install-noprint">
        <CopyLink url={url} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()}>Print this page</button>
      </div>
    </div>
  );
}

export default function InstallPage() {
  const platform = getPlatform();
  const url = `${window.location.origin}/install`;
  const [installed, setInstalled] = useState(() => isStandalone());
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <div className="install-page">
      <div className="card install-card">
        <img className="install-logo" src={previuLogo} alt="Previu" />
        <h1 className="install-title">Add Previu to your phone</h1>
        <p className="notice-text">
          Open Previu like any other app, with one tap from your Home Screen. No app store needed.
        </p>

        {installed ? (
          <div className="alert alert-success" role="status">
            Previu is already installed on this device.
          </div>
        ) : platform === 'ios-safari' ? <IosSafariSteps />
          : platform === 'ios-other' ? <IosOtherSteps url={url} />
          : platform === 'android-chrome' ? <AndroidSteps canPrompt={!!deferred} onInstall={install} url={url} />
          : platform === 'android-webview' ? <AndroidSteps webview url={url} />
          : <DesktopQr url={url} />}

        <div className="install-actions install-noprint">
          <Link className="btn btn-ghost btn-sm" to="/">Back to Previu</Link>
        </div>
      </div>
    </div>
  );
}
